import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtCurrency(n) {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  if (Math.abs(v) >= 1e9) return `$${(v/1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v/1e6).toFixed(1)}M`;
  return `$${Math.round(v).toLocaleString()}`;
}
function fmtPct(n) { if (n === null || n === undefined) return "—"; return `${(Number(n) * 100).toFixed(2)}%`; }

function TrustBadge({ score }) {
  if (!score) return null;
  const colors = { green: "bg-emerald-600 text-white", amber: "bg-amber-500 text-white", red: "bg-rose-600 text-white" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono ${colors[score.color]}`}
          title={`parent_ein_match: ${score.counts.parent_ein_match}, manual_seed: ${score.counts.manual_seed}, name_pattern_match: ${score.counts.name_pattern_match}`}
          data-testid="system-trust-score">
      TRUST {(score.trust_score * 100).toFixed(0)}%
    </span>
  );
}

export default function SystemDetail() {
  const { ein } = useParams();
  const [sys, setSys] = useState(null);
  const [members, setMembers] = useState([]);
  const [trust, setTrust] = useState(null);
  const [sortBy, setSortBy] = useState("total_beds");
  const [sortDir, setSortDir] = useState("desc");
  const cycleSort = (col) => {
    if (sortBy !== col) { setSortBy(col); setSortDir("desc"); return; }
    setSortDir(sortDir === "desc" ? "asc" : "desc");
  };
  const sortIndicator = (col) => sortBy === col ? (sortDir === "desc" ? " ↓" : " ↑") : "";
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, m, t] = await Promise.all([
          axios.get(`${API}/systems/${ein}`, { withCredentials: true }),
          axios.get(`${API}/systems/${ein}/members?limit=200`, { withCredentials: true }),
          axios.get(`${API}/systems/${ein}/trust-score`, { withCredentials: true }),
        ]);
        setSys(s.data); setMembers(m.data.members || []); setTrust(t.data);
      } catch (e) { setErr(e?.response?.data?.detail || e.message); }
      finally { setLoading(false); }
    })();
  }, [ein]);

  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;
  if (err) return <div className="p-8 text-sm text-red-700">{err}</div>;
  if (!sys) return null;

  const sortedMembers = [...members].sort((a, b) => {
    const dir = sortDir === "desc" ? -1 : 1;
    if (sortBy === "total_beds") return dir * ((a.total_beds || 0) - (b.total_beds || 0));
    if (sortBy === "state") return dir * (a.state || "").localeCompare(b.state || "");
    if (sortBy === "city") return dir * (a.city || "").localeCompare(b.city || "");
    if (sortBy === "name") return dir * (a.name || "").localeCompare(b.name || "");
    if (sortBy === "ccn") return dir * (a.ccn || "").localeCompare(b.ccn || "");
    return 0;
  });
  const trend = sys.trend || [];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="system-page">
      <Link to="/" className="text-emerald-700 text-xs hover:text-emerald-800">&larr; back to search</Link>

      {/* Data freshness ribbon */}
      <div className="mt-3 mb-2 px-3 py-2 bg-zinc-50 border border-zinc-200 rounded text-[11px] font-mono text-zinc-600 flex flex-wrap gap-x-4"
           data-testid="data-freshness-ribbon">
        <span>System 990: {trend.length ? `TY${trend[trend.length-1].tax_year}–TY${trend[0].tax_year}` : "—"}</span>
        <span className="text-zinc-400">·</span>
        <span>Members: {sys.member_count} ({trust?.color || "—"} trust)</span>
        <span className="text-zinc-400">·</span>
        <span className="text-zinc-400">IRS Form 990 via ProPublica Nonprofit Explorer + manual_systems.csv crosswalk</span>
      </div>

      {/* Identity strip */}
      <div className="mt-3" data-testid="system-identity">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">{sys.system_name || sys.organization_name}</h1>
          <span className="font-mono text-zinc-600">EIN {sys.ein}</span>
          {sys.latest_tax_year && <span className="badge badge-muted font-mono">TY{sys.latest_tax_year}</span>}
          <span className="badge badge-muted font-mono" data-testid="system-member-count">{sys.member_count} members</span>
          {trust && <TrustBadge score={trust} />}
        </div>
        {sys.note && <div className="mt-2 text-sm text-amber-700">{sys.note}</div>}
      </div>

      {/* Financial trend */}
      {trend.length > 0 && (
        <div className="mt-6 border border-zinc-200 rounded-lg bg-white" data-testid="system-financial-trend">
          <div className="px-3 py-2 border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500">5-year IRS Form 990 consolidated financials</div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr><th className="text-left px-3 py-2 text-xs font-mono text-zinc-500">Metric</th>
                {trend.map(t => <th key={t.tax_year} className="text-right px-3 py-2 text-xs font-mono text-zinc-500">TY{t.tax_year}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-zinc-100"><td className="px-3 py-2 font-mono text-xs">Total revenue</td>
                {trend.map(t => <td key={t.tax_year} className="text-right px-3 py-2 font-mono text-xs">{fmtCurrency(t.total_revenue)}</td>)}</tr>
              <tr className="border-t border-zinc-100"><td className="px-3 py-2 font-mono text-xs">Total expenses</td>
                {trend.map(t => <td key={t.tax_year} className="text-right px-3 py-2 font-mono text-xs">{fmtCurrency(t.total_expenses)}</td>)}</tr>
              <tr className="border-t border-zinc-100"><td className="px-3 py-2 font-mono text-xs">Net income</td>
                {trend.map(t => <td key={t.tax_year} className="text-right px-3 py-2 font-mono text-xs">{fmtCurrency(t.net_income)}</td>)}</tr>
              <tr className="border-t border-zinc-100 bg-emerald-50/30"><td className="px-3 py-2 font-mono text-xs font-semibold">Operating margin</td>
                {trend.map(t => <td key={t.tax_year} className="text-right px-3 py-2 font-mono text-xs">{fmtPct(t.operating_margin)}</td>)}</tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Member roster */}
      <div className="mt-6 border border-zinc-200 rounded-lg bg-white" data-testid="system-member-roster">
        <div className="px-3 py-2 border-b border-zinc-200 flex justify-between items-baseline">
          <div className="text-[10px] uppercase font-mono text-zinc-500">Member hospitals · {sys.member_count} total</div>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  className="text-xs border border-zinc-300 rounded px-2 py-1 font-mono" data-testid="sort-select">
            <option value="total_beds">Sort by beds</option>
            <option value="state">Sort by state</option>
            <option value="name">Sort by name</option>
          </select>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left text-xs font-mono text-zinc-500">
              <th className="px-3 py-2 cursor-pointer hover:text-emerald-700" onClick={() => cycleSort("ccn")} data-testid="system-member-sort-ccn">CCN{sortIndicator("ccn")}</th>
              <th className="px-3 py-2 cursor-pointer hover:text-emerald-700" onClick={() => cycleSort("name")} data-testid="system-member-sort-name">Name{sortIndicator("name")}</th>
              <th className="px-3 py-2 cursor-pointer hover:text-emerald-700" onClick={() => cycleSort("city")} data-testid="system-member-sort-city">City{sortIndicator("city")}</th>
              <th className="px-3 py-2 cursor-pointer hover:text-emerald-700" onClick={() => cycleSort("state")} data-testid="system-member-sort-state">State{sortIndicator("state")}</th>
              <th className="text-right px-3 py-2 cursor-pointer hover:text-emerald-700" onClick={() => cycleSort("total_beds")} data-testid="system-member-sort-beds">Beds{sortIndicator("total_beds")}</th>
              <th className="px-3 py-2">FY</th>
              <th className="px-3 py-2">Source</th>
            </tr>
          </thead>
          <tbody>
            {sortedMembers.map(m => (
              <tr key={m.ccn} className="border-t border-zinc-100 hover:bg-emerald-50 cursor-pointer"
                  onClick={() => window.location.href = `/hospitals/${m.ccn}`}
                  data-testid={`system-member-row-${m.ccn}`}>
                <td className="px-3 py-2 font-mono text-xs">{m.ccn}</td>
                <td className="px-3 py-2 text-xs">{m.name || "—"}</td>
                <td className="px-3 py-2 text-xs">{m.city || "—"}</td>
                <td className="px-3 py-2 text-xs">{m.state || "—"}</td>
                <td className="text-right px-3 py-2 font-mono text-xs">{m.total_beds ?? "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">FY{m.latest_fy || "—"}</td>
                <td className="px-3 py-2"><span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700">{m.source}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <button disabled className="border border-zinc-300 px-3 py-2 text-xs font-mono rounded bg-zinc-100 text-zinc-400 cursor-not-allowed" data-testid="system-pdf-btn"
                title="Multi-page system scorecard PDF — implementation queued for Phase 3 follow-up">
          Download system scorecard (PDF) — coming soon
        </button>
      </div>

      <div className="mt-8 pt-4 border-t border-zinc-200 text-[10px] font-mono text-zinc-400">
        Trust score weights authority of each member's `source`: parent_ein_match=1.0, manual_seed=0.8, name_pattern_match=0.5, ein_candidate=0.3. After Schedule H ingest in Phase 3 Tier B, name_pattern_match members will be promoted to parent_ein_match and this score will jump.
      </div>
    </div>
  );
}
